/**
 * Registering the service worker, and asking for persistent storage.
 *
 * Both are start-up chores that belong outside React: they run once, they do not
 * render anything, and a component that owns them would re-run them on every
 * remount in StrictMode.
 */

/**
 * Register at `/app` scope.
 *
 * The worker file sits at the origin root, so the scope has to be declared
 * explicitly — a worker at `/sw.js` defaults to controlling the whole origin,
 * which would put the marketing site under it too. The site is server-rendered
 * HTML that wants ordinary HTTP caching, not an app shell.
 */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return;
  // Vite's dev server does not serve `/public/sw.js` the way production does,
  // and a stale dev worker is a confusing way to spend an afternoon.
  if (import.meta.env.DEV) return;

  const register = () => {
    void navigator.serviceWorker.register('/sw.js', { scope: '/app' }).catch((error: Error) => {
      /*
       * Registration fails on an insecure origin, on an untrusted certificate,
       * in some private modes, and whenever a user has disabled it. None of
       * that stops the app working — it stops it working OFFLINE, which is
       * worth knowing but not worth interrupting someone about on arrival.
       *
       * Logged rather than swallowed, though. The commonest cause during
       * development is a self-signed certificate the device has not been told
       * to trust, and the message says exactly that — silence there costs an
       * afternoon.
       */
      if (!import.meta.env.PROD || window.location.hostname !== 'www.twocoins.app') {
        console.warn('TwoCoins: the service worker did not register —', error.message);
      }
    });
  };

  /*
   * Wait for `load`, unless it has already happened.
   *
   * `addEventListener('load')` added AFTER the event has fired never runs, and
   * this module is imported from a deferred module script — so on a warm cache
   * or a fast local server the page can be complete before this line executes.
   * The symptom is the worst kind: no error, no registration, and offline and
   * install silently absent on exactly the fastest connections.
   */
  if (document.readyState === 'complete') register();
  else window.addEventListener('load', register, { once: true });
}

/**
 * Ask the browser not to evict the ledger.
 *
 * The rule from the handoff: request at install, and again at thirty ledger
 * entries — then never again. Two asks, at the two moments the answer is most
 * likely to be yes, and no nagging in between.
 *
 * Chrome grants it silently based on engagement rather than prompting, so this
 * is usually invisible. Firefox prompts. Safari ignores it and uses its own
 * seven-day eviction rules, which is exactly why CSV export exists.
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!navigator.storage?.persist) return false;
  try {
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
